import axios from 'axios';
import { PostcardTemplate, Template } from '../types/template';
import { RecipientData } from '../types/order';
import { Postcard } from '../types/postcard';

// Create an axios instance with the base URL from environment variables
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  headers: {
    'Content-Type': 'application/json',
  },
  // Add these options to handle file uploads
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// Add a request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const postcardApi = {
  getAll: () => api.get<Postcard[]>('/postcards'),
  getById: (id: string) => api.get<Postcard>(`/postcards/${id}`),
  getNearby: (lat: number, lng: number, radius: number = 10) => 
    api.get<Postcard[]>(`/postcards/nearby?lat=${lat}&lng=${lng}&radius=${radius}`),
  create: (data: {
    templateId: string;
    imageUrl: string;
    referenceImageUrl?: string;
  }) => api.post('/postcards', data),
  generate: (formData: FormData) => api.post('/postcards/generate', formData, {
    headers: {
      // Don't set Content-Type, let the browser set it with the boundary
    },
    transformRequest: [(data) => data],
    timeout: 120000
  }),
  update: (id: string, data: FormData) => api.put(`/postcards/${id}`, data, {
    headers: {
      // Don't set Content-Type, let the browser set it with the boundary
    },
    transformRequest: [(data) => data]
  }),
  delete: (id: string) => api.delete(`/postcards/${id}`),
};

export const orderApi = {
  create: (data: any) => api.post('/orders', data),
  getById: (id: string) => api.get(`/orders/${id}`),
  getUserOrders: async () => {
    const response = await api.get('/orders');
    // Transform any image URLs in the orders
    if (response.data.orders) {
      response.data.orders = response.data.orders.map((order: any) => {
        if (order.items) {
          order.items = order.items.map((item: any) => {
            if (item.imageUrl) {
              item.imageUrl = getFileUrl(item.imageUrl);
            }
            return item;
          });
        }
        return order;
      });
    }
    return response;
  },
  createCheckoutSession: (data: {
    postcardId: string;
    senderAddress: any;
    recipientData: RecipientData[];
    amount: number;
  }) => {
    return api.post('/orders/create-checkout-session', data);
  },
};

// Utility function to prefix URLs with the API URL if they're relative paths
export const getFileUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  
  // If it's already an absolute URL, return it as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Otherwise, prefix with the API URL
  const apiUrl = process.env.REACT_APP_IMAGES_URL || '';
  return `${apiUrl}${url.startsWith('/') ? url : `/${url}`}`;
};

// Modify the userApi object to use the getFullUrl function
export const userApi = {
  login: (credentials: any) => api.post('/auth/login', credentials),
  register: (userData: any) => api.post('/auth/register', userData),
  getProfile: async () => {
    const response = await api.get('/users/profile');
    // Transform the avatarUrl in the response
    if (response.data.user && response.data.user.avatarUrl) {
      response.data.user.avatarUrl = getFileUrl(response.data.user.avatarUrl);
    }
    return response;
  },
  updateProfile: (profileData: any) => api.put('/users/profile', profileData),
  changePassword: (passwordData: { currentPassword: string; newPassword: string }) => 
    api.put('/users/profile/password', passwordData),
  uploadAvatar: (avatar: File) => {
    const formData = new FormData();
    formData.append('avatar', avatar);
    
    // Log the file for debugging
    console.log('Uploading file:', avatar.name, 'size:', avatar.size, 'type:', avatar.type);
    
    return api.post('/users/profile/avatar', formData, {
      headers: {
        // Don't set Content-Type, let the browser set it with the boundary
      },
      // Add these options to ensure proper file upload
      transformRequest: [(data) => data], // Don't transform FormData
      timeout: 30000 // 30 seconds timeout
    });
  },
  createTokenPurchaseSession: (packageId: string) => 
    api.post('/tokens/purchase', { packageId }),
  getTokenBalance: () => 
    api.get('/tokens/balance'),
};

export const cartApi = {
  getCart: () => api.get('/cart'),
  addToCart: (item: any) => api.post('/cart', item),
  updateCartItem: (itemId: string, quantity: number) => 
    api.put(`/cart/${itemId}`, { quantity }),
  removeFromCart: (itemId: string) => api.delete(`/cart/${itemId}`),
  clearCart: () => api.delete('/cart'),
};

export const templateApi = {
  getTemplates: () => api.get<Template[]>('/templates'),
  getTemplate: (id: string) => api.get<Template>(`/templates/${id}`),
  createTemplate: (formData: FormData) => api.post<Template>('/templates', formData, {
    headers: {},
    transformRequest: [(data) => data],
    timeout: 30000
  }),
  updateTemplate: (id: string, formData: FormData) => 
    api.put<Template>(`/templates/${id}`, formData, {
      headers: {},
      transformRequest: [(data) => data],
      timeout: 30000
    }),
  deleteTemplate: (id: string) => api.delete(`/templates/${id}`),
  getPostcardTemplates: () => api.get<PostcardTemplate>('/templates/postcard-templates'),
};

export const adminApi = {
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getUsers: () => api.get('/users'),
  updateUser: (userId: string, userData: any) => api.put(`/users/${userId}`, userData),
};

export default api; 